// Telemetria do fluxo de prescrição.
//
// Motivo de existir: os três caminhos de falha de PrescriptionView terminavam
// todos na MESMA frase ("Houve um problema ao tentar carregar a página"), sem
// `captureException` em lugar nenhum. O Sentry já está inicializado em
// `main.js`, mas nunca recebia nada daqui — então um relato de médico não
// tinha como ser reduzido a uma causa: falha de API, script da Memed que não
// carregou e exceção na montagem do módulo eram indistinguíveis.
//
// Fica separado de `utils/memed.js` de propósito: aquele módulo é DOM puro e
// o teste dele não carrega Sentry.
import * as Sentry from "@sentry/vue";

// Código curto o bastante para o médico ler em voz alta no suporte. O mapa é
// consultado primeiro pela CAUSA do erro e só depois pelo estágio, porque as
// causas `memed:*` são mais específicas do que o estágio onde apareceram.
export const MEMED_FAILURE_CODES = {
  // Estágios (falha nossa, antes ou depois da Memed)
  "room-fetch": "RX-01",
  "patient-fetch": "RX-02",
  "module-open": "RX-06",
  "module-ready": "RX-07",
  "module-command": "RX-08",
  "broadcast-channel": "RX-11",

  // Causas vindas de `ensureMemedSdk` / `resolveMemedToken`
  "memed:script-error": "RX-03",
  "memed:hub-timeout": "RX-04",
  "memed:no-token": "RX-05",
  "memed:unavailable": "RX-09",
  "memed:not-approved": "RX-10"
};

export const MEMED_UNKNOWN_CODE = "RX-00";

export function memedFailureCode(stage, error) {
  const cause = (error && error.message) || "";
  return (
    MEMED_FAILURE_CODES[cause] ||
    MEMED_FAILURE_CODES[stage] ||
    MEMED_UNKNOWN_CODE
  );
}

// Nem toda desistência chega com um Error: o deadline de 20s do módulo dispara
// sem argumento nenhum. Normalizar aqui é o que garante que o Sentry receba um
// stack e não a string "undefined".
function normalizeError(stage, error) {
  if (error instanceof Error) return error;
  if (error) return new Error(String(error));
  return new Error(`memed:${stage}`);
}

/**
 * Manda a falha para o Sentry com estágio, causa e código nas tags, e devolve
 * o código para a tela exibir. Nunca lança: telemetria quebrada não pode ser o
 * motivo de o médico ficar sem receita.
 */
export function reportMemedFailure(stage, error, context = {}) {
  const code = memedFailureCode(stage, error);

  try {
    const normalized = normalizeError(stage, error);
    const status = error && error.response ? error.response.status : undefined;

    Sentry.withScope(scope => {
      scope.setTag("flow", "memed-prescription");
      scope.setTag("memed.stage", stage);
      scope.setTag("memed.code", code);
      scope.setTag("memed.cause", normalized.message);
      if (status) scope.setTag("memed.http_status", String(status));
      scope.setContext("memed", { stage, code, status, ...context });
      Sentry.captureException(normalized);
    });
  } catch (reportingError) {
    // Ambiente sem Sentry inicializado (teste, build local) não é falha.
  }

  return code;
}
